/**
 * @fileoverview CommandPalette — global Cmd+K / Ctrl+K search modal
 *
 * Mounted once at the dashboard layout level. Listens for Cmd+K
 * (macOS) and Ctrl+K (Windows/Linux) and opens a modal containing
 * the `UniversalSearchBar` in workspace scope, so the user can
 * navigate or ask Morgan from anywhere in the app.
 *
 * - Pressing Escape closes it.
 * - Clicking a search hit closes it and navigates.
 * - Clicking "Ask Morgan" closes it and navigates to /dashboard/agent
 *   with the prefilled query handed off via location-state-like flow
 *   (the workspace agent route reads `?ask=...` and seeds the chat).
 *
 * Gated to `super_admin` while the Morgan-only rollout is in effect
 * (same gate as the Agent sidebar entry).
 */

import { useEffect, useState, useCallback, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/ui/composed/overlays/dialog";
import { UniversalSearchBar } from "./UniversalSearchBar";
import { useActiveOrg } from "@/utils/org-context";
import {
  CommandPaletteContext,
  useCommandPalette,
} from "./CommandPaletteContext";

/**
 * Wrap the dashboard layout once. Provides `useCommandPalette()` to any
 * descendant so external triggers (sidebar item, empty-state CTA, etc.)
 * can open the palette without needing a ref or prop chain.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  return (
    <CommandPaletteContext.Provider
      value={{ open, setOpen, openPalette, closePalette, toggle }}
    >
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function CommandPalette() {
  const { open, setOpen, toggle, closePalette } = useCommandPalette();
  const { activeOrg } = useActiveOrg();
  const navigate = useNavigate();

  // Global keyboard shortcut — Cmd+K (mac) / Ctrl+K (win/linux).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const isModK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (isModK) {
        e.preventDefault();
        toggle();
      } else if (e.key === "Escape" && open) {
        closePalette();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, toggle, closePalette]);

  const handleAskAi = useCallback(
    (query: string) => {
      closePalette();
      // Forward the query to the workspace agent route as a URL search
      // param — the route reads `ask` on mount and seeds the chat.
      navigate({
        to: "/dashboard/agent",
        search: { ask: query },
      });
    },
    [navigate, closePalette],
  );

  if (!activeOrg) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        // Spotlight-style top placement: anchor near the top of the
        // viewport (override the centered default via `top-[15%]` +
        // `translate-y-0`) and clamp total height so the embedded
        // results panel scrolls inside the modal rather than running
        // off the page. `flex flex-col min-h-0` is the wrapper for the
        // input (shrink-0) above a scrollable results region.
        className="top-[15%] flex max-h-[70vh] min-h-0 max-w-2xl translate-y-0 flex-col gap-0 overflow-hidden bg-popover p-0 sm:rounded-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        {/* Hidden a11y title/description — Radix requires them but the
            palette UI is self-explanatory and we don't want extra
            chrome above the input. */}
        <DialogTitle className="sr-only">Search and ask Morgan</DialogTitle>
        <DialogDescription className="sr-only">
          Quick search across jobs, contacts, and documents, or ask Morgan a
          question.
        </DialogDescription>

        <div className="flex min-h-0 flex-col p-3">
          <UniversalSearchBar
            orgId={activeOrg._id}
            onAskAi={handleAskAi}
            onResultClick={closePalette}
            placeholder="Search jobs, contacts, documents… or ask Morgan"
            autoFocus
            askButtonLabel="Ask Morgan"
            embedded
            className="flex min-h-0 flex-1 flex-col"
          />
          <p className="mt-2 shrink-0 px-1 text-[11px] text-muted-foreground/70">
            <kbd className="rounded border border-border bg-card px-1 py-0.5 text-[10px] font-medium">
              ⏎
            </kbd>{" "}
            sends to Morgan ·{" "}
            <kbd className="rounded border border-border bg-card px-1 py-0.5 text-[10px] font-medium">
              Esc
            </kbd>{" "}
            to close
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
